// ============================================================================
// TEMPLATES API CLIENT v2
// Con endpoint de cálculo de precios
// ============================================================================

import { api } from './client';

// ----------------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------------

export interface PriceInfo {
  finalPrice: number;
  source: 'act' | 'site' | 'tariff' | 'override' | 'none';
  sourceLabel: string;
  actPrice: number;
  sitePrice: number | null;
  tariffPrice: number | null;
  overridePrice: number | null;
}

export interface TariffInfo {
  id: string;
  code: string;
  name: string;
  policyName: string;
  insuranceCompanyName: string;
}

export interface SocietyInfo {
  id: string;
  code: string;
  name: string;
}

export interface TemplateItem {
  id: string;
  medicalActId: string;
  medicalActCode: string;
  medicalActName: string;
  treatmentGroupName: string;
  subspecialtyName: string;
  specialtyName: string;
  quantity: number;
  sortOrder: number;
  notes: string | null;
  priceOverride: number | null;
  priceInfo: PriceInfo;
  tariffId: string | null;
  tariffInfo: TariffInfo | null;
  societyId: string | null;
  societyInfo: SocietyInfo | null;
  availableSocieties: SocietyInfo[];
  requiresOdontogram: boolean;
  odontogramType: string | null;
  lineSubtotal: number;
}

export interface Template {
  id: string;
  companyId: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  itemsCount: number;
  linkedSitesCount: number;
  items?: TemplateItem[];
  estimatedTotal?: number;
}

export interface TemplateSelect {
  id: string;
  code: string;
  name: string;
  itemsCount: number;
}

export interface MissingActInfo {
  actId: string;
  actCode: string;
  actName: string;
  defaultPrice: number;
  reason: 'not_linked' | 'inactive';
}

export interface TemplateLinkedSite {
  siteId: string;
  siteCode: string;
  siteName: string;
  isLinked: boolean;
  isActive: boolean;
  missingActs: MissingActInfo[];
  canLink: boolean;
  availableSocieties: SocietyInfo[];
  defaultSocietyId: string | null;
}

export interface PaginatedTemplates {
  data: Template[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface TemplatePreview {
  templateId: string;
  templateCode: string;
  templateName: string;
  siteId: string | null;
  siteName: string | null;
  lines: {
    medicalActCode: string;
    medicalActName: string;
    quantity: number;
    unitPrice: number;
    priceSource: string;
    tariffName: string | null;
    societyName: string | null;
    lineTotal: number;
    notes: string | null;
  }[];
  subtotal: number;
  totalItems: number;
  totalQuantity: number;
  // FIX P1/P2: Sociedades del centro para el desplegable de cada línea
  siteSocieties: Array<{ id: string; code: string; name: string; isDefault: boolean }>;
  siteDefaultSocietyId: string | null;
}

// Respuesta del cálculo de precio
export interface CalculatedPrice {
  price: number;
  source: 'act' | 'site' | 'tariff' | 'none';
  sourceLabel: string;
  tariffId: string | null;
  tariffName: string | null;
}

// ----------------------------------------------------------------------------
// INPUT TYPES
// ----------------------------------------------------------------------------

export interface TemplateItemInput {
  medicalActId: string;
  tariffId?: string | null;
  societyId?: string | null;
  quantity?: number;
  priceOverride?: number | null;
  notes?: string;
  sortOrder?: number;
}

export interface CreateTemplateInput {
  code: string;
  name: string;
  description?: string;
  items?: TemplateItemInput[];
  sortOrder?: number;
}

export interface UpdateTemplateInput {
  code?: string;
  name?: string;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateTemplateItemsInput {
  items: TemplateItemInput[];
}

export interface SiteLinkInput {
  siteId: string;
  isLinked: boolean;
}

export interface UpdateSiteLinksInput {
  links: SiteLinkInput[];
}

export interface QuickLinkActInput {
  medicalActId: string;
  siteId: string;
  priceOverride?: number;
  societyIds?: string[];
}

export interface TemplateQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'code' | 'createdAt' | 'sortOrder';
  sortOrder?: 'asc' | 'desc';
  includeInactive?: boolean;
  includeItems?: boolean;
  siteId?: string;
}

// ----------------------------------------------------------------------------
// API FUNCTIONS
// ----------------------------------------------------------------------------

export const templatesApi = {
  // Listar plantillas (paginado)
  async getAll(params: TemplateQueryParams = {}): Promise<PaginatedTemplates> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.search) searchParams.append('search', params.search);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.append('includeInactive', 'true');
    if (params.includeItems) searchParams.append('includeItems', 'true');
    if (params.siteId) searchParams.append('siteId', params.siteId);

    const query = searchParams.toString();
    const response = await api.get<PaginatedTemplates>(`/templates${query ? `?${query}` : ''}`);
    return response;
  },

  // Obtener para select
  async getForSelect(siteId?: string): Promise<TemplateSelect[]> {
    const query = siteId ? `?siteId=${siteId}` : '';
    return api.get<TemplateSelect[]>(`/templates/select${query}`);
  },

  // Obtener tarifas disponibles
  async getAvailableTariffs(): Promise<TariffInfo[]> {
    return api.get<TariffInfo[]>('/templates/tariffs');
  },

  // Obtener sociedades disponibles
  async getAvailableSocieties(): Promise<SocietyInfo[]> {
    return api.get<SocietyInfo[]>('/templates/societies');
  },

  // Calcular precio de un acto
  async calculatePrice(
    medicalActId: string,
    tariffId?: string,
    siteId?: string,
  ): Promise<CalculatedPrice> {
    const params = new URLSearchParams();
    params.append('medicalActId', medicalActId);
    if (tariffId) params.append('tariffId', tariffId);
    if (siteId) params.append('siteId', siteId);
    return api.get<CalculatedPrice>(`/templates/calculate-price?${params.toString()}`);
  },

  // Obtener tarifa por defecto
  async getDefaultTariff(): Promise<{ tariffId: string | null }> {
    return api.get<{ tariffId: string | null }>('/templates/default-tariff');
  },

  // Obtener una plantilla
  async getById(id: string, siteId?: string): Promise<Template> {
    const query = siteId ? `?siteId=${siteId}` : '';
    return api.get<Template>(`/templates/${id}${query}`);
  },

  // Obtener previsualización
  async getPreview(id: string, siteId?: string): Promise<TemplatePreview> {
    const query = siteId ? `?siteId=${siteId}` : '';
    return api.get<TemplatePreview>(`/templates/${id}/preview${query}`);
  },

  // Crear plantilla
  async create(data: CreateTemplateInput): Promise<Template> {
    return api.post<Template>('/templates', data);
  },

  // Actualizar plantilla
  async update(id: string, data: UpdateTemplateInput): Promise<Template> {
    return api.patch<Template>(`/templates/${id}`, data);
  },

  // Actualizar líneas
  async updateItems(id: string, data: UpdateTemplateItemsInput): Promise<Template> {
    return api.put<Template>(`/templates/${id}/items`, data);
  },

  // Desactivar
  async deactivate(id: string): Promise<Template> {
    return api.delete<Template>(`/templates/${id}`);
  },

  // Reactivar
  async reactivate(id: string): Promise<Template> {
    return api.patch<Template>(`/templates/${id}/reactivate`, {});
  },

  // Obtener centros vinculados
  async getLinkedSites(id: string): Promise<TemplateLinkedSite[]> {
    return api.get<TemplateLinkedSite[]>(`/templates/${id}/linked-sites`);
  },

  // Actualizar vinculaciones
  async updateSiteLinks(id: string, data: UpdateSiteLinksInput): Promise<{ success: boolean; message: string }> {
    return api.put<{ success: boolean; message: string }>(`/templates/${id}/site-links`, data);
  },

  // Vincular acto a centro rápidamente
  async quickLinkAct(data: QuickLinkActInput): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>('/templates/quick-link-act', data);
  },

  // Exportar (descarga binaria → fetch directo, mismo patrón que budgetsApi.downloadPdf)
  async exportExcel(): Promise<Blob> {
    const token = localStorage.getItem('imi_access_token') || '';
    const response = await fetch('/api/v1/templates/export', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const err = await response.text().catch(() => 'Error desconocido');
      throw new Error(`Error al exportar plantillas: ${err}`);
    }
    return response.blob();
  },

  // Importar (multipart/form-data → fetch directo, mismo patrón que budgetsApi.accept)
  async importExcel(file: File): Promise<{ created: number; updated: number; errors: string[] }> {
    const token = localStorage.getItem('imi_access_token') || '';
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('/api/v1/templates/import', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al importar plantillas' }));
      throw new Error(err.message || 'Error al importar plantillas');
    }
    return response.json();
  },
};

export default templatesApi;
